/**
 * Route Configuration
 *
 * Defines all application routes including current and planned routes.
 * Uses RouteObject type from react-router-dom for type safety.
 *
 * Code Splitting Strategy:
 * - Heavy routes (/games) are lazy-loaded to reduce initial bundle size
 * - Light routes (/, /login) are eagerly loaded for instant navigation
 */

import { Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { LandingPage } from '@/features/landing';
import { LoginPage, BridgeLoginPage } from '@/features/auth';
import { LoadingScreen } from '@/shared/components/LoadingScreen';
import { lazyWithRetry } from '@/shared/utils/lazyWithRetry';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Lazy-load heavy routes with auto-retry on chunk load errors
const GamePage = lazyWithRetry(() =>
  import('@/features/game').then((module) => ({ default: module.GamePage }))
);

const DailyPodiumPage = lazyWithRetry(
  () => import('@/features/daily-rewards/components/DailyPodiumPage')
);

// Temporary stub component for unimplemented routes
function ComingSoonPage() {
  return (
    <div className="flex-1 w-full flex items-center justify-center">
      <div className="text-white text-2xl font-bold">Coming Soon</div>
    </div>
  );
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LandingPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    )
  },
  {
    path: '/auth/bridge-login',
    element: <BridgeLoginPage />
  },
  {
    path: '/games',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingScreen />}>
          <GamePage />
        </Suspense>
      </ProtectedRoute>
    )
  },
  {
    // TODO: Replace with features/lobby/components/LobbyPage.tsx
    path: '/lobby',
    element: (
      <ProtectedRoute>
        <ComingSoonPage />
      </ProtectedRoute>
    )
  },
  {
    // TODO: Replace with features/game/components/GamePage.tsx with dynamic game ID
    path: '/game/:id',
    element: (
      <ProtectedRoute>
        <ComingSoonPage />
      </ProtectedRoute>
    )
  },
  {
    // TODO: Replace with features/profile/components/ProfilePage.tsx
    path: '/profile',
    element: (
      <ProtectedRoute>
        <ComingSoonPage />
      </ProtectedRoute>
    )
  },
  {
    // TODO: Replace with features/leaderboard/components/LeaderboardPage.tsx
    path: '/leaderboard',
    element: (
      <ProtectedRoute>
        <ComingSoonPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/daily-rewards/podium',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingScreen />}>
          <DailyPodiumPage />
        </Suspense>
      </ProtectedRoute>
    )
  }
];
